import React, { useState } from 'react';
import { Form, Button, Container, Card, Alert, Modal, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import './Login.css';
import { getAuthHeaders } from '../../utils/authUtils';

const Register = ({ onRegister }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    username: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [registeredUsername, setRegisteredUsername] = useState('');
  const [termsContent, setTermsContent] = useState('');
  const [termsLoading, setTermsLoading] = useState(false);
  const [termsError, setTermsError] = useState('');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const validateForm = () => {
    // Reset error
    setError('');

    // Check if username (email) is provided
    if (!form.username.trim()) {
      setError('Email is required.');
      return false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.username)) {
      setError('Please enter a valid email address.');
      return false;
    }

    // Check password length
    if (form.password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return false;
    }

    // Check password strength (at least one letter and one number)
    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#?&\-_+=(){}\[\]|:;"'<>,.\/\\]{6,}$/;
    if (!passwordRegex.test(form.password)) {
      setError('Password must contain at least one letter and one number.');
      return false;
    }

    // Check if passwords match
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match.');
      return false;
    }

    // Check if terms are accepted
    if (!acceptTerms) {
      setError('You must accept the Terms & Conditions to register.');
      return false;
    }

    return true;
  };

  const fetchTermsAndConditions = async () => {
    setTermsLoading(true);
    setTermsError('');
    
    try {
      // Using a CORS proxy to fetch the terms content
      // Note: In production, you might need to set up your own proxy or configure CORS on the target server
      const proxyUrl = 'https://api.allorigins.win/get?url=';
      const targetUrl = encodeURIComponent('https://skedguy.com/terms-of-service');
      
      const response = await fetch(proxyUrl + targetUrl);
      
      if (!response.ok) {
        throw new Error('Failed to fetch terms and conditions');
      }
      
      const data = await response.json();
      
      // Parse the HTML content to extract readable text
      const parser = new DOMParser();
      const doc = parser.parseFromString(data.contents, 'text/html');
      
      // Remove script tags and other unwanted elements
      const scripts = doc.querySelectorAll('script, style, nav, header, footer');
      scripts.forEach(el => el.remove());
      
      // Get the main content (adjust selector based on the actual structure)
      const mainContent = doc.querySelector('main, .content, .terms-content, article') || doc.body;
      
      if (mainContent) {
        setTermsContent(mainContent.innerHTML);
      } else {
        setTermsContent(data.contents);
      }
      
    } catch (error) {
      console.error('Error fetching terms:', error);
      setTermsError('Failed to load Terms & Conditions. Please try again or visit https://skedguy.com/terms-of-service directly.');
      
      // Fallback: provide a link to the terms page
      setTermsContent(`
        <div style="text-align: center; padding: 20px;">
          <p>Unable to load the Terms & Conditions content.</p>
          <p>Please visit the following link to view the terms:</p>
          <a href="https://skedguy.com/terms-of-service" target="_blank" rel="noopener noreferrer">
            https://skedguy.com/terms-of-service
          </a>
        </div>
      `);
    } finally {
      setTermsLoading(false);
    }
  };

  const handleTermsClick = async (e) => {
    e.preventDefault();
    setShowTermsModal(true);
    
    if (!termsContent) {
      await fetchTermsAndConditions();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Only send username and password to the server
      const registrationData = {
        username: form.username,
        password: form.password
      };

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/users`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(registrationData)
      });
      
      const data = await response.json();

      if (response.ok || response.status === 201) {
        // If the API returns a JWT token, log the user in immediately (mirror Login.js behavior)
        if (data.jwtToken) {
          try {
            localStorage.setItem('jwtToken', data.jwtToken);
            localStorage.setItem('tokenType', data.tokenType || 'Bearer');
            if (data.id) localStorage.setItem('userId', data.id);
            if (data.role) localStorage.setItem('role', data.role);
            if (data.username) localStorage.setItem('username', data.username);
            localStorage.setItem('activeSubscription', data.activeSubscription ? 'true' : 'false');
            if (data.stripeCustomerId) localStorage.setItem('stripeCustomerId', data.stripeCustomerId);
            if (data.expiryDate) localStorage.setItem('expiryDate', data.expiryDate);

            // Optional: set a token expiration (1 hour from now)
            const expirationTime = new Date().getTime() + 60 * 60 * 1000;
            localStorage.setItem('tokenExpiration', expirationTime);
          } catch (err) {
            console.warn('Could not persist auth info to localStorage', err);
          }

          // Notify parent component of login (without sensitive fields)
          const userWithoutPassword = { ...data };
          delete userWithoutPassword.password;
          delete userWithoutPassword.jwtToken;

          if (onRegister) {
            onRegister(userWithoutPassword);
          }

          // Navigate to dashboard (assumption: '/' is the dashboard route)
          navigate('/');

        } else {
          // No token returned: keep previous behavior and ask user to login
          setRegisteredUsername(form.username);
          setSuccess(`User ${form.username} created successfully. Please login.`);
          setForm({
            username: '',
            password: '',
            confirmPassword: ''
          });
          setAcceptTerms(false);
        }
      } else {
        setError(data.message || 'Registration failed.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Container className="login-container d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <Card className="login-card" style={{ width: '400px' }}>
          <Card.Body className="text-center">
            <img src="images/logo.png" alt="SkedGuy" className="mb-3" style={{ width: '100%' }} />
            <Alert variant="success">{success}</Alert>
            <Button className="w-100" onClick={() => navigate('/login')}>
              Go to Login
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="login-container d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
      <Card className="login-card" style={{ width: '400px' }}>
        <Card.Body className="text-center">
          <img src="images/logo.png" alt="SkedGuy" className="mb-3" style={{ width: '100%' }} />
          <h3 className="mb-3">Register</h3>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-2">
              <Form.Control
                type="email"
                name="username"
                placeholder="Email Address"
                value={form.username}
                onChange={handleChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Control
                type="password"
                name="password"
                placeholder="Password (min 6 characters)"
                value={form.password}
                onChange={handleChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Control
                type="password"
                name="confirmPassword"
                placeholder="Confirm Password"
                value={form.confirmPassword}
                onChange={handleChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="terms-checkbox"
                style={{ marginLeft: '1rem', textAlign: 'left' }}
                label={
                  <span>
                    I accept the{' '}
                    <a 
                      href="#" 
                      onClick={handleTermsClick}
                      style={{ color: '#007bff', textDecoration: 'underline' }}
                    >
                      Terms & Conditions
                    </a>
                  </span>
                }
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                required
              />
            </Form.Group>
            <Button type="submit" className="w-100" disabled={loading}>
              {loading ? 'Registering...' : 'Register'}
            </Button>
          </Form>
          <Button
            variant="link"
            className="mt-3"
            onClick={() => navigate('/login')}
          >
            Already have an account? Login
          </Button>
        </Card.Body>
      </Card>

      {/* Terms & Conditions Modal */}
      <Modal show={showTermsModal} onHide={() => setShowTermsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Terms & Conditions</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {termsLoading ? (
            <div className="text-center p-4">
              <Spinner animation="border" role="status" />
              <p className="mt-2">Loading Terms & Conditions...</p>
            </div>
          ) : termsError ? (
            <Alert variant="warning">
              {termsError}
            </Alert>
          ) : (
            <div 
              dangerouslySetInnerHTML={{ __html: termsContent }}
              style={{ 
                fontSize: '14px',
                lineHeight: '1.5'
              }}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTermsModal(false)}>
            Close
          </Button>
          {!termsError && (
            <Button 
              variant="primary" 
              onClick={() => {
                setAcceptTerms(true);
                setShowTermsModal(false);
              }}
              disabled={termsLoading}
            >
              Accept Terms
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Register;